import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtClock } from "../lib/format";
import { Icon } from "./icons";

/** Personal markers on a voice track (shared with the Mobile app): save the current second, jump back, loop to the next one. */
export function MarkersPanel({ songId, voice, position, onSeek, onLoop }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["markers", songId], queryFn: () => api(`/songs/${songId}/markers`) });
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [err, setErr] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["markers", songId] });
  const add = useMutation({
    mutationFn: () => api(`/songs/${songId}/markers`, { method: "POST", body: JSON.stringify({ voice, position, label: label.trim() }) }),
    onSuccess: () => { setAdding(false); setLabel(""); setErr(""); refresh(); },
    onError: (e) => setErr(e.message),
  });
  const remove = useMutation({ mutationFn: (id) => api(`/markers/${id}`, { method: "DELETE" }), onSuccess: refresh });
  const mine = (q.data || []).filter((m) => m.voice === voice).sort((a, b) => a.position - b.position);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="song-markers">
      <div className="row-between">
        <span className="t-kicker">MARCADORES · {voice?.toUpperCase()}</span>
        <button className="chip" onClick={() => setAdding((v) => !v)} data-testid="markers-add">
          <Icon name={adding ? "close" : "bookmark-outline"} size={14} /> {adding ? "CANCELAR" : `MARCAR ${fmtClock(position)}`}
        </button>
      </div>
      {adding ? (
        <form onSubmit={(e) => { e.preventDefault(); add.mutate(); }} style={{ display: "flex", gap: 8 }}>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} autoFocus placeholder={`Ex.: entrada do ${voice?.toLowerCase()} (${fmtClock(position)})`} style={{ flex: 1, minWidth: 0 }} data-testid="markers-label-input" />
          <button className="icon-btn" type="submit" disabled={add.isPending} aria-label="Guardar marcador" data-testid="markers-save" style={{ background: "var(--brand-primary)" }}><Icon name="checkmark" size={18} color="var(--on-brand)" /></button>
        </form>
      ) : null}
      {err ? <div className="t-hint" style={{ color: "var(--error)" }}>{err}</div> : null}
      {mine.length ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {mine.map((m, i) => (
            <span key={m.id} className="chip" style={{ gap: 4, paddingRight: 4 }} data-testid={`markers-item-${m.id}`}>
              <button onClick={() => onSeek(m.position)} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", display: "flex", gap: 6 }} data-testid={`markers-go-${m.id}`}>
                <b>{fmtClock(m.position)}</b> {m.label || "Marcador"}
              </button>
              <button className="icon-btn" style={{ width: 24, height: 24 }} onClick={() => onLoop(m.position, mine[i + 1]?.position ?? null)} aria-label="Repetir até ao próximo marcador" data-testid={`markers-loop-${m.id}`}><Icon name="repeat" size={13} color="var(--brand-primary)" /></button>
              <button className="icon-btn" style={{ width: 24, height: 24 }} onClick={() => remove.mutate(m.id)} aria-label="Apagar marcador" data-testid={`markers-delete-${m.id}`}><Icon name="close" size={13} /></button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
